import React from 'react';
import { portfolioData } from '../data/mock';
import { Github, Linkedin, Figma, Instagram, MessageCircle, ExternalLink, Radio, ArrowUpRight } from 'lucide-react';
import useAnalytics from '../hooks/useAnalytics';
import DecryptedText from './reactbits/DecryptedText';
import SpotlightCard from './reactbits/SpotlightCard';
import BlurText from './reactbits/BlurText';
import './Connect.css';

// Custom Discord SVG Icon
const DiscordIcon = (props) => (
  <svg
    viewBox="0 0 127.14 96.36"
    width="1em"
    height="1em"
    fill="currentColor"
    {...props}
  >
    <path d="M107.7,8.07A105.15,105.15,0,0,0,77.26,0a77.19,77.19,0,0,0-3.3,6.83A96.67,96.67,0,0,0,53.22,6.83,77.19,77.19,0,0,0,49.88,0,105.15,105.15,0,0,0,19.44,8.07C3.66,31.58-1.86,54.65,1,77.53A105.73,105.73,0,0,0,32,96.36a77.7,77.7,0,0,0,6.63-10.85,68.43,68.43,0,0,1-10.5-5c.88-.65,1.72-1.34,2.51-2a68.1,68.1,0,0,0,76.07,0c.79.71,1.63,1.4,2.52,2a68.43,68.43,0,0,1-10.5,5,77.7,77.7,0,0,0,6.63,10.85,105.73,105.73,0,0,0,31.59-18.83C129.24,48.24,122.9,25.43,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53S36.18,40.36,42.45,40.36,53.83,46,53.83,53,48.72,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.24,60,73.24,53S78.41,40.36,84.69,40.36,96.07,46,96.07,53,91,65.69,84.69,65.69Z" />
  </svg>
);

const Connect = () => {
  const { trackEvent } = useAnalytics();
  const { connect } = portfolioData;

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'github':
        return <Github className="platform-hero-icon" />;
      case 'linkedin':
        return <Linkedin className="platform-hero-icon" />;
      case 'figma':
        return <Figma className="platform-hero-icon" />;
      case 'instagram':
        return <Instagram className="platform-hero-icon" />;
      case 'whatsapp':
        return <MessageCircle className="platform-hero-icon" />;
      case 'discord':
        return <DiscordIcon className="platform-hero-icon" />;
      default:
        return <ExternalLink className="platform-hero-icon" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'online': return '#00FFD1';
      case 'active': return '#00D4FF';
      case 'direct': return '#50fa7b';
      default: return '#6FD2C0';
    }
  };

  // Quadruple array to create infinite seamless loop
  const loopCards = [...connect.links, ...connect.links, ...connect.links, ...connect.links];

  return (
    <section className="connect-section" id="connect">
      {/* Background Pattern */}
      <div className="connect-bg-pattern"></div>

      <div className="connect-container">
        {/* Header */}
        <div className="section-header">
          <div className="header-line"></div>
          <h2 className="section-title">
            <span className="title-bracket">{'['}</span>
            <DecryptedText text={connect.title} speed={40} maxIterations={12} animateOn="view" />
            <span className="title-bracket">{']'}</span>
          </h2>
          <div className="section-subtitle">
            <BlurText text={connect.subtitle} delay={60} animateBy="words" />
          </div>
        </div>

        {/* Connect Cards Infinite Logo Loop Container */}
        <div className="connect-cards-loop-container">
          <div className="connect-cards-loop-track">
            {loopCards.map((link, index) => {
              const statusColor = getStatusColor(link.status);
              const nodeNum = (index % connect.links.length) + 1;

              return (
                <div key={`${link.id}-${index}`} className="connect-cyber-card-slide">
                  <SpotlightCard
                    className="connect-cyber-card"
                    spotlightColor="rgba(0, 255, 209, 0.14)"
                  >
                    <div className="connect-card-inner">
                      {/* Top Status & Badge Bar */}
                      <div className="card-top-status-bar">
                        <div className="badge-tag-pill">
                          <Radio className="badge-signal-icon" />
                          <span>{link.badge?.toUpperCase()}</span>
                        </div>

                        <div className="live-status-pill">
                          <span className="live-pulse-dot" style={{ backgroundColor: statusColor }}></span>
                          <span className="status-text">{link.status}</span>
                        </div>
                      </div>

                      {/* Cover Image Container */}
                      <div className={`connect-media-box ${link.platform.toLowerCase()}-cover`}>
                        <img
                          src={link.coverImage}
                          alt={link.title}
                          className="connect-cover-img"
                          loading="lazy"
                        />
                        <div className="cover-scanlines"></div>
                        <div className="cover-platform-badge">
                          {getPlatformIcon(link.platform)}
                          <span className="platform-name">{link.platform}</span>
                        </div>
                      </div>

                      {/* Body Content */}
                      <div className="connect-card-body">
                        <div className="connect-title-row">
                          <h3 className="connect-item-title">{link.title}</h3>
                          <span className="protocol-tag">NODE_0{nodeNum}</span>
                        </div>

                        <p className="connect-item-desc">{link.description}</p>

                        {/* Action Link Button */}
                        <div className="connect-action-row">
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="connect-protocol-btn"
                            onClick={() => trackEvent('social_click', { platform: link.platform, url: link.url })}
                          >
                            <span>{link.buttonText?.toUpperCase()}</span>
                            <ArrowUpRight className="btn-arrow-icon" />
                          </a>
                        </div>
                      </div>

                      {/* Corner Cyber Accents */}
                      <div className="corner-accent top-l"></div>
                      <div className="corner-accent top-r"></div>
                      <div className="corner-accent bot-l"></div>
                      <div className="corner-accent bot-r"></div>
                    </div>
                  </SpotlightCard>
                </div>
              );
            })}
          </div>

          <div className="cards-loop-fade left"></div>
          <div className="cards-loop-fade right"></div>
        </div>
      </div>
    </section>
  );
};

export default Connect;
